import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Banknote, CheckCircle2, AlertTriangle, ExternalLink, RefreshCw, Loader2, ShieldCheck, Percent,
  Wallet, Link2, Save,
} from "lucide-react";

const Row = ({ label, ok, hint }) => (
  <div className="flex items-center gap-2 py-1.5" data-testid={`sc-check-${label.toLowerCase().replace(/\s/g, "-")}`}>
    {ok ? <CheckCircle2 className="h-4 w-4 text-[#059669]" /> : <AlertTriangle className="h-4 w-4 text-[#F59E0B]" />}
    <span className="text-sm flex-1">{label}</span>
    {hint && <span className="text-[10px] text-[#868E96]">{hint}</span>}
  </div>
);

export default function ChefEncaissement() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pp, setPp] = useState(null);
  const [ppForm, setPpForm] = useState({ mode: "off", env: "live", client_id: "", client_secret: "", paypal_me_url: "" });
  const [ppBusy, setPpBusy] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await api.get("/chef/stripe-connect");
      setData(d);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const loadPp = async () => {
    try {
      const { data: d } = await api.get("/chef/paypal-connect");
      setPp(d);
      setPpForm((f) => ({
        ...f, mode: d.mode || "off", env: d.env || "live",
        paypal_me_url: d.paypal_me_url || "", client_id: "", client_secret: "",
      }));
    } catch (e) { /* silencieux */ }
  };

  useEffect(() => { load(); loadPp(); }, []);

  const savePp = async () => {
    setPpBusy(true);
    try {
      const { data: d } = await api.put("/chef/paypal-connect", ppForm);
      setPp(d);
      setPpForm((f) => ({ ...f, client_secret: "" }));
      toast.success(d.ready ? "PayPal prêt à encaisser" : "Réglages PayPal enregistrés");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPpBusy(false); }
  };

  const onboard = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/chef/stripe-connect/onboard");
      window.location.assign(d.url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };

  const openDashboard = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/chef/stripe-connect/dashboard");
      window.open(d.url, "_blank", "noopener");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const st = data?.status || {};
  const fee = data?.settings || {};
  const feeLabel = (fee.fee_percent || fee.fee_fixed_cents)
    ? [fee.fee_percent ? `${fee.fee_percent} %` : null,
       fee.fee_fixed_cents ? `${(fee.fee_fixed_cents / 100).toFixed(2)} €` : null]
      .filter(Boolean).join(" + ")
    : null;

  return (
    <div data-testid="chef-encaissement-page">
      <PageHeader
        eyebrow="Encaissement"
        title="Recevoir l'argent de mes réservations"
        sub="Reliez votre compte bancaire pour que les paiements de vos clients arrivent directement chez vous. PAG ne détient jamais vos fonds."
        right={<Button variant="outline" onClick={load} data-testid="sc-reload" className="h-10">
          <RefreshCw className="h-4 w-4 mr-1" />Actualiser
        </Button>}
      />

      <div className="p-6 lg:p-10 max-w-3xl space-y-6">
        {data && !data.platform_configured && (
          <div className="border-2 border-[#F59E0B] bg-[#FFFBEB] p-5" data-testid="sc-platform-missing">
            <div className="flex items-center gap-2 font-display font-bold">
              <AlertTriangle className="h-5 w-5 text-[#F59E0B]" />Paiement en ligne pas encore ouvert
            </div>
            <div className="text-sm text-[#495057] mt-1">
              {data.reason} Vos clients peuvent déjà réserver et régler <b>sur place</b> :
              ils reçoivent leur QR code par email dans tous les cas.
            </div>
          </div>
        )}

        {data?.platform_configured && !data.connected && (
          <div className="border-2 border-[#0A0A0A] bg-white p-6" data-testid="sc-not-connected">
            <div className="flex items-start gap-3">
              <Banknote className="h-7 w-7 text-[#635BFF] shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-display font-black text-xl tracking-tight">Connecter mon compte d'encaissement</div>
                <div className="text-sm text-[#495057] mt-1">
                  Vous serez redirigé vers Stripe pour renseigner votre entreprise et votre IBAN.
                  Comptez 5 minutes. Ensuite, <b>chaque réservation payée en ligne arrive sur votre compte
                  bancaire</b>, virée automatiquement par Stripe.
                </div>
                <ul className="text-[11px] text-[#495057] mt-3 space-y-1">
                  <li>• Vous restez propriétaire de votre compte Stripe et de vos fonds.</li>
                  <li>• PAG ne voit jamais vos coordonnées bancaires.</li>
                  <li>• Vous gardez votre historique même si vous quittez PAG.</li>
                  {feeLabel && <li>• Commission PAG par réservation : <b>{feeLabel}</b>.</li>}
                </ul>
                <Button onClick={onboard} disabled={busy} data-testid="sc-onboard"
                        className="mt-4 h-12 px-6 bg-[#635BFF] hover:bg-[#4F46E5] text-white font-bold">
                  {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                  {busy ? "Ouverture de Stripe…" : "Connecter mon compte bancaire"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {data?.connected && (
          <div className={`border-2 p-6 ${data.ready ? "border-[#059669] bg-[#ECFDF5]" : "border-[#F59E0B] bg-[#FFFBEB]"}`}
               data-testid="sc-connected">
            <div className="flex items-center gap-2 font-display font-black text-xl tracking-tight">
              {data.ready
                ? <><CheckCircle2 className="h-6 w-6 text-[#059669]" />Prêt à encaisser</>
                : <><AlertTriangle className="h-6 w-6 text-[#F59E0B]" />Configuration à terminer</>}
            </div>
            <div className="text-sm text-[#495057] mt-1">
              {data.ready
                ? "Vos clients peuvent payer en ligne. L'argent arrive sur votre compte Stripe puis sur votre banque."
                : "Stripe attend encore des informations. Tant que ce n'est pas terminé, seul le paiement sur place est proposé à vos clients."}
            </div>

            <div className="mt-4 border border-[#DEE2E6] bg-white p-4">
              <Row label="Dossier envoyé à Stripe" ok={st.details_submitted} />
              <Row label="Encaissement autorisé" ok={st.charges_enabled} />
              <Row label="Virements bancaires autorisés" ok={st.payouts_enabled} />
              {st.country && <Row label={`Pays : ${st.country.toUpperCase()}`} ok hint={st.default_currency?.toUpperCase()} />}
            </div>

            {st.currently_due?.length > 0 && (
              <div className="mt-3 text-xs bg-white border border-[#F59E0B] p-3" data-testid="sc-due">
                <div className="font-bold uppercase tracking-wider text-[10px] text-[#92400E] mb-1">
                  Informations réclamées par Stripe
                </div>
                <ul className="list-disc pl-4 text-[#495057] space-y-0.5">
                  {st.currently_due.slice(0, 8).map((d) => <li key={d}><code className="text-[10px]">{d}</code></li>)}
                </ul>
              </div>
            )}
            {st.disabled_reason && (
              <div className="mt-2 text-xs text-[#7F1D1D]" data-testid="sc-disabled-reason">
                Motif de blocage Stripe : <code>{st.disabled_reason}</code>
              </div>
            )}

            <div className="flex flex-wrap gap-2 mt-4">
              {!data.ready && (
                <Button onClick={onboard} disabled={busy} data-testid="sc-continue"
                        className="bg-[#635BFF] text-white font-bold">
                  {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Terminer ma configuration Stripe
                </Button>
              )}
              <Button variant="outline" onClick={openDashboard} disabled={busy} data-testid="sc-dashboard">
                <ExternalLink className="h-4 w-4 mr-1" />Mes paiements et virements
              </Button>
            </div>

            {feeLabel && (
              <div className="mt-3 text-[11px] text-[#495057] flex items-center gap-1" data-testid="sc-fee">
                <Percent className="h-3 w-3" />Commission PAG retenue par réservation : <b>{feeLabel}</b>
              </div>
            )}
            <div className="mt-1 font-mono text-[10px] text-[#868E96]" data-testid="sc-account-id">
              {data.account_id}
            </div>
          </div>
        )}

        {/* ================= PAYPAL ================= */}
        <div className="border-2 border-[#003087] bg-white p-6" data-testid="pp-card">
          <div className="flex items-start gap-3">
            <Wallet className="h-7 w-7 text-[#003087] shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-display font-black text-xl tracking-tight">Encaisser aussi par PayPal</div>
              <div className="text-sm text-[#495057] mt-1">
                Vos clients paient avec leur compte PayPal, <b>l'argent arrive sur votre compte PayPal</b>.
                Deux façons de faire, à vous de choisir.
              </div>

              <div className="flex flex-wrap gap-2 mt-4" data-testid="pp-modes">
                {[
                  { k: "off", l: "Désactivé" },
                  { k: "api", l: "Mes identifiants PayPal" },
                  { k: "link", l: "Mon lien PayPal.Me" },
                ].map((m) => (
                  <button key={m.k} type="button" data-testid={`pp-mode-${m.k}`}
                          onClick={() => setPpForm({ ...ppForm, mode: m.k })}
                          className={`text-[11px] uppercase tracking-wider font-bold px-3 py-2 border ${
                            ppForm.mode === m.k ? "bg-[#003087] text-white border-[#003087]" : "bg-white text-[#868E96] border-[#DEE2E6]"}`}>
                    {m.l}
                  </button>
                ))}
              </div>

              {ppForm.mode === "api" && (
                <div className="mt-4 space-y-3" data-testid="pp-api-form">
                  <div className="text-[11px] text-[#495057] bg-[#F8F9FA] border border-[#DEE2E6] p-3">
                    <b>Paiement confirmé automatiquement.</b> Créez une application REST sur{" "}
                    <a href="https://developer.paypal.com/dashboard/applications/live" target="_blank"
                       rel="noopener noreferrer" className="underline text-[#002FA7]" data-testid="pp-guide-link">
                      developer.paypal.com <ExternalLink className="inline h-3 w-3" />
                    </a>{" "}
                    avec votre compte PayPal Business, puis copiez ici le Client ID et le Secret.
                    PAG ne peut rien faire d'autre que créer vos encaissements : vos fonds restent chez vous.
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider">Client ID</Label>
                      <Input value={ppForm.client_id} data-testid="pp-client-id"
                             placeholder={pp?.client_id_masked || "AXx…"}
                             onChange={(e) => setPpForm({ ...ppForm, client_id: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider">Secret</Label>
                      <Input type="password" value={ppForm.client_secret} data-testid="pp-client-secret"
                             placeholder={pp?.has_secret ? "•••••••• (inchangé)" : "EL2…"}
                             onChange={(e) => setPpForm({ ...ppForm, client_secret: e.target.value })} />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[{ k: "live", l: "Compte réel (Live)" }, { k: "sandbox", l: "Test (Sandbox)" }].map((e2) => (
                      <button key={e2.k} type="button" data-testid={`pp-env-${e2.k}`}
                              onClick={() => setPpForm({ ...ppForm, env: e2.k })}
                              className={`text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 border ${
                                ppForm.env === e2.k ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#868E96] border-[#DEE2E6]"}`}>
                        {e2.l}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {ppForm.mode === "link" && (
                <div className="mt-4 space-y-2" data-testid="pp-link-form">
                  <div className="text-[11px] text-[#495057] bg-[#FFFBEB] border border-[#F59E0B] p-3">
                    <b>Le plus simple, mais vérification manuelle.</b> Le client paie via votre lien
                    PayPal.Me, puis vous confirmez l'encaissement d'un clic sur l'écran Réservations
                    (bouton « Encaissé »). Le QR code reste la preuve de réservation.
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider flex items-center gap-1">
                      <Link2 className="h-3 w-3" />Votre lien PayPal.Me
                    </Label>
                    <Input value={ppForm.paypal_me_url} placeholder="https://paypal.me/votrenom"
                           data-testid="pp-me-url"
                           onChange={(e) => setPpForm({ ...ppForm, paypal_me_url: e.target.value })} />
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3 mt-4">
                <Button onClick={savePp} disabled={ppBusy} data-testid="pp-save"
                        className="h-11 px-5 bg-[#003087] hover:bg-[#001F5C] text-white font-bold">
                  {ppBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  {ppBusy ? "Vérification chez PayPal…" : "Enregistrer PayPal"}
                </Button>
                {pp && (
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 ${
                    pp.ready ? "bg-[#059669] text-white" : "bg-[#868E96] text-white"}`} data-testid="pp-status">
                    {pp.ready
                      ? (pp.usable_mode === "api" ? "Identifiants validés par PayPal" : "Lien PayPal actif")
                      : "PayPal non actif"}
                  </span>
                )}
              </div>
              <div className="text-[11px] text-[#868E96] mt-2">
                N'oubliez pas d'activer « PayPal » dans les réglages de réservation pour le proposer à vos clients.
              </div>
            </div>
          </div>
        </div>

        <div className="border border-[#DEE2E6] bg-white p-5 text-xs text-[#495057]" data-testid="sc-help">
          <div className="font-display font-bold text-sm mb-2">Comment circule l'argent ?</div>
          <ol className="list-decimal pl-4 space-y-1">
            <li>Votre client paie sa réservation sur votre mini-site.</li>
            <li>Le paiement est encaissé <b>sur votre compte Stripe</b>, pas sur celui de PAG.</li>
            <li>Stripe vire le montant sur votre compte bancaire selon votre calendrier de versement.</li>
            <li>En cas de remboursement, il est débité de votre solde Stripe.</li>
          </ol>
          <div className="mt-2 text-[10px] text-[#868E96]">
            Vous préférez tout encaisser à la réception ? Désactivez simplement le paiement en ligne
            dans les réglages de réservation : le QR code sert alors de preuve de réservation.
          </div>
        </div>
      </div>
    </div>
  );
}
